import { FC } from 'react';
import { Tr } from '@utils';
import { Permission, User, UserInvitation } from '@services';
import { Grid, Typography } from '@mui/material';
import { EntityList, EntityListItem, EntityListItemCell, IEntityListProps } from '@components/common';
import { useLoggedinUser } from '@hooks/useLoggedingUser';

type EntityType = UserInvitation | User;

export interface ICollaboratorListProps extends IEntityListProps<EntityType> {
	paddingX?: number | string;
	entities: EntityType[];
	selected: EntityType[];
	onSelectChange?: (selected: EntityType[]) => void;
	onEdit?: (entity: EntityType) => void;
	onDelete?: (entity: EntityType) => void;
	onResendInvitation?: (entity: EntityType) => void;
}

export const CollaboratorList: FC<ICollaboratorListProps> = ({
	entities,
	selected,
	onSelectChange,
	onEdit,
	onDelete,
	onResendInvitation,
	paddingX,
	...props
}) => {
	const user = useLoggedinUser();
	const canManageCollaborators = user?.canManageCollaborators();
	const hasWritePermission = user?.hasPermission(Permission.USER_WRITE);
	const handleSelectChange = (status: boolean, entity: EntityType) => {
		const newSelections = selected.filter((s) => s.email !== entity.email);
		status && newSelections.push(entity);
		onSelectChange?.(newSelections);
	};

	return (
		<EntityList
			entities={entities}
			selected={selected}
			onSelectChange={onSelectChange}
			hideSelectAllButton={!hasWritePermission}
			paddingX={paddingX}
			{...props}
		>
			{entities.map((entity, i) => (
				<EntityListItem
					key={`entity-${entity.email}-${i}`}
					sx={{ paddingRight: paddingX }}
					selected={!!selected.find((s) => s.email === entity.email)}
					notSelectable={!hasWritePermission}
					actions={[
						{ text: <Tr.Portal path="edit" />, onClick: () => onEdit?.(entity), hidden: !canManageCollaborators },
						{ text: <Tr.Portal path="delete" />, onClick: () => onDelete?.(entity), hidden: !hasWritePermission },
						...(onResendInvitation
							? [{ text: <Tr.Portal path="collaborators.resend-invitation" />, onClick: () => onResendInvitation(entity) }]
							: [])
					]}
					onSelectChange={(status) => handleSelectChange(status, entity)}
				>
					<Grid container columnSpacing={2}>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path="collaborators.first-name" />}>
								<Typography>{entity.name}</Typography>
							</EntityListItemCell>
						</Grid>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path="collaborators.last-name" />}>
								<Typography>{entity.surname}</Typography>
							</EntityListItemCell>
						</Grid>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path="collaborators.email" />}>
								<Typography>{entity.email}</Typography>
							</EntityListItemCell>
						</Grid>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path="collaborators.role" />}>
								<Typography>{entity.role?.name || ''}</Typography>
							</EntityListItemCell>
						</Grid>
					</Grid>
				</EntityListItem>
			))}
		</EntityList>
	);
};
